import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { InlineSpinner } from './app/shared/components/common/spinner/Spinner';

import Forbidden from './pages/error/Forbidden';
import NotFound from './pages/error/NotFound';
import RouteErrorBoundary from './pages/error/RouteErrorBoundary';

const ProtectedLayout = lazy(() => import('./layouts/ProtectedLayout'));
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const OnboardingPage = lazy(() => import('./app/onboarding/components/OnboardingPage'));
const ConsultaExternaPage = lazy(() => import('./pages/public/consultaExterna/ConsultaExterna'));

// Páginas legales (cumplimiento normativo mexicano)
const AvisoPrivacidadPage = lazy(() => import('./pages/legal/AvisoPrivacidad'));
const TerminosCondicionesPage = lazy(() => import('./pages/legal/TerminosCondiciones'));
const AdministracionPage = lazy(() => import('./app/administracion/pages/AdministracionPage'));
const UsuariosPage = lazy(() => import('./app/usuarios/pages/UsuariosPage'));
const ParametrosPage = lazy(() => import('./app/parametros/pages/ParamametrosPage'));
const TarificacionPage = lazy(() => import('./app/tarificacion/TarificacionPage'));
const HomePage = lazy(() => import('./app/home/pages/HomePage'));
const DashboardPage = lazy(() => import('./app/dashboard/pages/DashboardPage'));
const MonitoreoPage = lazy(() => import('./app/monitoreo/pages/MonitoreoPage'));
const SesionOperadoresPage = lazy(() => import('./app/monitoreo/pages/SesionOperadoresPage'));
const GruasDinamicasPage = lazy(() => import('./app/gruasDinamicas/pages/GruasDinamicasPage'));
const ConfiguracionPage = lazy(() => import('./app/configuracion/ConfiguracionPage'));
const CostoPage = lazy(() => import('./app/tarificacion/pages/CostoPage'));
const FacturacionPage = lazy(() => import('./app/facturacion/pages/FacturacionPage'));
const ExcedentesPage = lazy(() => import('./app/excedentes/pages/ExcedentesPage'));
const SubscripcionPage = lazy(() => import('./app/subscripcion/pages/SubscripcionPage'));
const CheckoutResultPage = lazy(() => import('./pages/public/CheckoutResultPage'));
const CombustiblePage = lazy(() => import('./app/combustible/pages/CombustiblePage'));
const NominaPage = lazy(() => import('./app/nomina/pages/NominaPage'));

// Catálogo pages
const CatalogoAditamentosPage = lazy(() => import('./app/catalogo/aditamentos/pages/CatalogoAditamentosPage'));
const CatalogoClientesPage = lazy(() => import('./app/catalogo/clientes/pages/CatalogoClientesPage'));
const CatalogoReglaCostosPage = lazy(() => import('./app/catalogo/reglaCostos/pages/CatalogoReglaCostosPage'));
const CatalogoEmpresaPage = lazy(() => import('./app/catalogo/empresa/pages/CatalogoEmpresaPage'));
const CatalogoPersonalPage = lazy(() => import('./app/catalogo/personal/pages/CatalogoPersonalPage'));
const CatalogoPoligonosPage = lazy(() => import('./app/catalogo/poligonos/pages/CatalogoPoligonosPage'));
const CatalogoRecursosPage = lazy(() => import('./app/catalogo/recursos/pages/CatalogoRecursosPage'));
const CatalogoServicioPage = lazy(() => import('./app/catalogo/servicio/pages/CatalogoServicioPage'));
const CatalogoTipoUsuariosPage = lazy(() => import('./app/catalogo/tipoUsuarios/pages/CatalogoTipoUsuariosPage'));
const CatalogoUnidadPage = lazy(() => import('./app/catalogo/unidadOperativa/pages/CatalogoUnidadOperativaPage'));
const CatalogoProveedoresCombustiblePage = lazy(() => import('./app/catalogo/proveedoresCombustible/pages/CatalogoProveedoresCombustiblePage'));

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => <Suspense fallback={<InlineSpinner />}>{children}</Suspense>;

const router = createBrowserRouter(
  [
    {
      path: '/',
      index: true,
      element: (
        <SuspenseWrapper>
          <LandingPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: 'login',
      element: (
        <SuspenseWrapper>
          <LoginPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: 'onboarding',
      element: (
        <SuspenseWrapper>
          <OnboardingPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: 'consultaExterna',
      element: (
        <SuspenseWrapper>
          <ConsultaExternaPage />
        </SuspenseWrapper>
      ),
    },
    // Páginas legales (cumplimiento normativo mexicano LFPDPPP, LFPC, NOM-151)
    {
      path: 'aviso-privacidad',
      element: (
        <SuspenseWrapper>
          <AvisoPrivacidadPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: 'terminos-condiciones',
      element: (
        <SuspenseWrapper>
          <TerminosCondicionesPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: 'checkout/success',
      element: (
        <SuspenseWrapper>
          <CheckoutResultPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: 'checkout/cancel',
      element: (
        <SuspenseWrapper>
          <CheckoutResultPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: '/',
      element: (
        <SuspenseWrapper>
          <ProtectedLayout />
        </SuspenseWrapper>
      ),
      errorElement: <RouteErrorBoundary />,
      children: [
        { path: 'home', element: <HomePage /> },
        { path: 'dashboard', element: <DashboardPage /> },
        { path: 'monitoreo', element: <MonitoreoPage /> },
        { path: 'monitoreo/sesion-operadores', element: <SesionOperadoresPage /> },
        { path: 'gruas-dinamicas', element: <GruasDinamicasPage /> },
        { path: 'administracion', element: <AdministracionPage /> },
        { path: 'usuarios', element: <UsuariosPage /> },
        { path: 'configuracion', element: <ConfiguracionPage /> },
        { path: 'parametros', element: <ParametrosPage /> },
        { path: 'tarificacion', element: <TarificacionPage /> },
        { path: 'tarificacion/costo', element: <CostoPage /> },
        { path: 'facturacion', element: <FacturacionPage /> },
        { path: 'administracion/excedentes', element: <ExcedentesPage /> },
        { path: 'administracion/combustible', element: <CombustiblePage /> },
        { path: 'administracion/nomina', element: <NominaPage /> },
        { path: 'catalogo/aditamentos', element: <CatalogoAditamentosPage /> },
        { path: 'catalogo/clientes', element: <CatalogoClientesPage /> },
        { path: 'catalogo/costos', element: <CatalogoReglaCostosPage /> },
        { path: 'catalogo/empresa', element: <CatalogoEmpresaPage /> },
        { path: 'catalogo/personal', element: <CatalogoPersonalPage /> },
        { path: 'catalogo/poligonos', element: <CatalogoPoligonosPage /> },
        { path: 'catalogo/recursos', element: <CatalogoRecursosPage /> },
        { path: 'catalogo/servicio', element: <CatalogoServicioPage /> },
        { path: 'catalogo/tipo-usuarios', element: <CatalogoTipoUsuariosPage /> },
        { path: 'catalogo/unidad', element: <CatalogoUnidadPage /> },
        { path: 'subscripcion', element: <SubscripcionPage /> },
        { path: 'catalogo/proveedores-combustible', element: <CatalogoProveedoresCombustiblePage /> },
      ],
    },
    { path: 'forbidden', element: <Forbidden /> },
    { path: '*', element: <NotFound /> },
  ],
  {}
);

export default router;
